import { ApplicationController } from "../application_controller";

// A table becomes cards on phones and tablets (table.table-cards in scss/components/_mobile.scss). The DOM is
// not rebuilt, so the desktop and the scripts that look for closest("tr") stay as they are:
//   - every cell gets data-label: the text of its column header ("Group · Column" under a two-row header),
//     a cell spanning several columns gets their common group label; in a totals row only such cells get one
//     (a total often stands under another column), set data-label in the markup where a total needs it;
//   - the role of a column is taken from th[data-card-role] and places the cell in the card:
//     icon, index (the row number, not shown on the card), title, status, actions, amount, comment, wide,
//     details (behind the "Details" button), hidden;
//   - rows get data-card-row: summary (totals, cells with colspan), nested (a table inside) or group
//     (set in the markup for a .d-lg-none group header row);
//   - empty cells are hidden on the card;
//   - a cell for the cards only is added in the markup as td.d-lg-none with data-label and data-card-role.
// A nested table needs its own table-cards class and controller.
export default class extends ApplicationController {
  connect() {
    const columns = this.headerColumns();
    this.bodyRows().forEach(row => this.markRowType(row));
    this.markCells(this.gridOf(this.bodyRows()), columns);
    this.bodyRows().forEach(row => this.addDetailsButton(row));
    this.releaseScrollWrappers();
  }

  // label and role per column index, built from the header rows with their rowspan and colspan
  headerColumns() {
    const columns = [];
    if (!this.element.tHead) return columns;

    this.gridOf(Array.from(this.element.tHead.rows)).forEach(({ cell, column, span }) => {
      const text = this.headerText(cell);
      for (let index = column; index < column + span; index++) {
        const entry = columns[index] || (columns[index] = { labels: [], role: null });
        if (text && !entry.labels.includes(text)) entry.labels.push(text);
        if (cell.dataset.cardRole) entry.role = cell.dataset.cardRole;
      }
    });
    return columns.map(entry => entry && { labels: entry.labels, role: entry.role });
  }

  // the text of a header cell; a line break (<br>) in a header becomes a space
  headerText(cell) {
    const copy = cell.cloneNode(true);
    copy.querySelectorAll("br").forEach(br => br.replaceWith(" "));
    return copy.textContent.replace(/\s+/g, " ").trim();
  }

  // own rows of the table: the rows of a nested table belong to its own controller
  bodyRows() {
    return Array.from(this.element.querySelectorAll(":scope > tbody > tr, :scope > tfoot > tr"));
  }

  // positions of the cells in the table grid, taking rowspan and colspan into account; a cell added for the cards
  // only (.d-lg-none, it has its label in the markup) takes no column, so the cells after it keep their headers
  gridOf(rows) {
    const taken = [];
    const cells = [];
    rows.forEach((row, row_index) => {
      const row_taken = taken[row_index] || (taken[row_index] = new Set());
      let column = 0;
      Array.from(row.cells).forEach(cell => {
        if (cell.classList.contains("d-lg-none")) return;
        while (row_taken.has(column)) column++;
        const span = cell.colSpan || 1;
        for (let below = 1; below < (cell.rowSpan || 1); below++) {
          const below_taken = taken[row_index + below] || (taken[row_index + below] = new Set());
          for (let index = column; index < column + span; index++) below_taken.add(index);
        }
        cells.push({ cell, column, span });
        column += span;
      });
    });
    return cells;
  }

  markCells(cells, columns) {
    cells.forEach(({ cell, column, span }) => {
      const spanned = columns.slice(column, column + span).filter(Boolean);
      const summary = cell.parentElement.dataset.cardRow === "summary";
      if (!cell.hasAttribute("data-label") && !(summary && span === 1)) {
        const label = span === 1 && spanned[0] ? spanned[0].labels.join(" · ") : this.commonLabel(spanned);
        if (label) cell.dataset.label = label;
      }
      if (!cell.dataset.cardRole && span === 1 && spanned[0] && spanned[0].role) {
        cell.dataset.cardRole = spanned[0].role;
      }
      if (this.isBlank(cell)) cell.classList.add("table-cards__blank");
    });
  }

  // "Amount" for the cells under "Amount · Account 1" and "Amount · Account 2"
  commonLabel(columns) {
    if (columns.length === 0) return "";
    const first = columns[0].labels;
    const common = [];
    for (let level = 0; level < first.length; level++) {
      if (!columns.every(column => column.labels[level] === first[level])) break;
      common.push(first[level]);
    }
    return common.join(" · ");
  }

  isBlank(cell) {
    return cell.textContent.trim() === "" &&
      !cell.querySelector("img, svg, i, input, select, textarea, button, a, table");
  }

  markRowType(row) {
    if (row.dataset.cardRow) return;

    if (row.querySelector(":scope > td table, :scope > th table")) row.dataset.cardRow = "nested";
    else if (row.classList.contains("total_row") || row.parentElement.tagName === "TFOOT" ||
      Array.from(row.cells).some(cell => cell.colSpan > 1)) row.dataset.cardRow = "summary";
  }

  // the "Details" button of a card with cells of the details role
  addDetailsButton(row) {
    if (row.dataset.cardRow || row.querySelector(":scope > .table-cards__more")) return;
    if (!row.querySelector(':scope > [data-card-role="details"]:not(.table-cards__blank)')) return;

    const cell = document.createElement("td");
    cell.className = "table-cards__more d-lg-none";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-link";
    button.setAttribute("aria-expanded", "false");
    button.textContent = document.body.dataset.mobileDetails;
    button.addEventListener("click", () => {
      const expanded = row.classList.toggle("is-expanded");
      button.setAttribute("aria-expanded", String(expanded));
      button.textContent = expanded ? document.body.dataset.mobileLess : document.body.dataset.mobileDetails;
    });
    cell.appendChild(button);
    row.appendChild(cell);
  }

  // the wrappers that scroll a wide table sideways on a computer let the cards take the screen width
  releaseScrollWrappers() {
    let node = this.element.parentElement;
    for (let level = 0; node && level < 3; level++, node = node.parentElement) {
      if (node.matches(".table-wrapper, .table-responsive")) node.classList.add("table-cards__wrapper");
    }
  }
}
